import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UserCheck, LayoutDashboard, MessageSquareText, CalendarClock } from 'lucide-react';

const featuresList = [
  {
    key: 'verified',
    icon: UserCheck,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
  },
  {
    key: 'campaigns',
    icon: LayoutDashboard,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
  },
  {
    key: 'communication',
    icon: MessageSquareText,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-100',
  },
  {
    key: 'interviews',
    icon: CalendarClock,
    color: 'text-jobisa-navy',
    bg: 'bg-slate-100',
    border: 'border-slate-200',
  },
];

const Features = () => {
  const { t } = useTranslation();

  return (
    <section id="features" className="py-24 bg-slate-50 relative border-t border-slate-200/60">
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="text-blue-600 font-semibold text-sm tracking-widest uppercase mb-4 block">
            Enterprise Capabilities
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-slate-900 mb-6 leading-tight">
            {t('features.title')}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            {t('features.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuresList.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
                className="group relative bg-white rounded-3xl p-8 border border-slate-200 hover:border-slate-300 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-1"
              >
                {/* Icon Container */}
                <div className={`w-14 h-14 rounded-2xl ${feature.bg} ${feature.border} border flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={24} className={feature.color} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {t(`features.items.${feature.key}.title`)}
                </h3>
                <p className="text-slate-600 leading-relaxed text-[15px]">
                  {t(`features.items.${feature.key}.desc`)}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
